import { useCallback, useEffect, useState } from 'react'
import {
  isPreferencesAction,
  PREFERENCES_NAVIGATE_CHANNEL,
  readPreferencesNavQuery,
  withoutPreferencesAction,
  type PreferencesAction,
  type PreferencesTarget,
} from '@/src/shared/preferencesNav'
import {
  DEFAULT_NAV,
  findTopNav,
  findVisibleSubNav,
  visibleTree,
  type SubNavId,
  type TopNavId,
} from './settings-tree'

const STORAGE_KEY = 'preferences.lastPath'

interface StoredNav {
  topNavId: TopNavId
  subNavId: SubNavId
}

interface NavState {
  topNavId: TopNavId
  subNavId: SubNavId
}

/**
 * Eine Aufgabe für die Seite (#158): `id` zählt hoch, damit dieselbe Aktion zweimal hintereinander (zweimal
 * „Backup wiederherstellen …“ im Menü) auch zweimal wirkt.
 */
export interface PendingPreferencesAction {
  action: PreferencesAction
  topNavId: TopNavId
  subNavId: SubNavId
  id: number
}

let actionCounter = 0

type Platform = 'electron' | 'web'

/**
 * Der erste Eintrag, den DIESE Plattform in diesem Reiter zeigt.
 *
 * Nötig, seit „App & Bedienung" im Web ausgeblendet ist (`visibleTree`): Ein
 * gespeicherter Pfad auf einen dort unsichtbaren Eintrag würde sonst eine
 * Seite öffnen, die in der Seitenleiste gar nicht steht.
 */
function firstVisibleSub(topNavId: TopNavId, platform: Platform): SubNavId {
  const tab = visibleTree(platform).find((t) => t.id === topNavId)
  return (tab?.subNav[0]?.id ?? DEFAULT_NAV.subNavId) as SubNavId
}

function readStored(platform: Platform): NavState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_NAV }
    const parsed = JSON.parse(raw) as Partial<StoredNav>
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_NAV }
    // Validate against the tree — IDs we no longer ship must not crash the
    // shell. Falls back to default on any mismatch. The `find` helpers
    // accept `string` at the boundary, so untrusted localStorage input is
    // safe; the return value is narrowed back to the tight union.
    const tab = findTopNav(String(parsed.topNavId ?? ''))
    if (!tab) return { ...DEFAULT_NAV }
    const sub = findVisibleSubNav(tab.id, String(parsed.subNavId ?? ''), platform)
    if (!sub) return { topNavId: tab.id, subNavId: firstVisibleSub(tab.id, platform) }
    return { topNavId: tab.id, subNavId: sub.id }
  } catch {
    return { ...DEFAULT_NAV }
  }
}

/**
 * Ein Ziel von außen (#262, Ablage › Importieren) — streng geprüft: Stimmt eine der beiden Ids nicht,
 * gilt es nicht, und das Fenster bleibt, wo es ist.
 */
function resolveTarget(target: unknown, platform: Platform): (NavState & { action?: PreferencesAction }) | null {
  if (!target || typeof target !== 'object') return null
  const { topNavId, subNavId, action } = target as Partial<PreferencesTarget>
  const tab = findTopNav(String(topNavId ?? ''))
  if (!tab) return null
  const sub = findVisibleSubNav(tab.id, String(subNavId ?? ''), platform)
  if (!sub) return null
  return isPreferencesAction(action) ? { topNavId: tab.id, subNavId: sub.id, action } : { topNavId: tab.id, subNavId: sub.id }
}

const pendingOf = (target: (NavState & { action?: PreferencesAction }) | null): PendingPreferencesAction | null =>
  target?.action ? { action: target.action, topNavId: target.topNavId, subNavId: target.subNavId, id: ++actionCounter } : null

// Nur das Einstellungsfenster in Electron: Im Web gehört die Adresse der App, und ein Menü gibt es dort nicht.
/** Das Ziel beim Öffnen: im Web vom Dialog, in Electron aus der Fenster-URL. */
function initialTargetOf(platform: Platform, given?: PreferencesTarget) {
  if (given) return resolveTarget(given, platform)
  return platform === 'electron' ? resolveTarget(readPreferencesNavQuery(window.location.search), platform) : null
}

function readInitial(platform: Platform, given?: PreferencesTarget): NavState {
  const target = initialTargetOf(platform, given)
  return target ? { topNavId: target.topNavId, subNavId: target.subNavId } : readStored(platform)
}

function writeStored(state: NavState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // SecurityError in restricted contexts — silently skip; navigation
    // still works for the current session.
  }
}

/**
 * Owns the `{topNavId, subNavId}` pair for the Settings shell. Persists to
 * localStorage so the user lands on the same SubPage next time they open
 * Settings. Invalid stored values fall back to {@link DEFAULT_NAV}.
 *
 * In Electron öffnet ein Ziel von außen eine bestimmte Seite: `?nav=top/sub` in der Fenster-URL beim
 * Start, `preferences:navigate` bei offenem Fenster (siehe `shared/preferencesNav`). Trägt das Ziel eine
 * `action`, reicht der Hook sie als {@link PendingPreferencesAction} an die Seite weiter.
 *
 * Switching the TopNav auto-resets `subNavId` to the first SubNav of the
 * newly active tab — matches the Figma "Wechsel auf Hauptmenü öffnet
 * dessen erstes Untermenü" expectation.
 */
export function usePreferencesNavigation(platform: Platform = 'electron', initialTarget?: PreferencesTarget) {
  const [state, setState] = useState<NavState>(() => readInitial(platform, initialTarget))
  // Das Ziel beim Öffnen zählt nur einmal; danach bringt jede Nachricht ihre eigene.
  const [action, setAction] = useState<PendingPreferencesAction | null>(() => pendingOf(initialTargetOf(platform, initialTarget)))

  useEffect(() => {
    writeStored(state)
  }, [state])

  // Die Aktion aus der Fenster-URL gilt einmal: ⌘R lädt dieselbe URL neu und öffnete sonst ungefragt wieder den Ablauf.
  useEffect(() => {
    if (platform !== 'electron' || initialTarget) return
    const search = withoutPreferencesAction(window.location.search)
    if (search === window.location.search) return
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${search}${window.location.hash}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur beim Öffnen des Fensters
  }, [])

  useEffect(() => {
    if (platform !== 'electron' || !window.api?.receive) return
    const unsubscribe = window.api.receive(PREFERENCES_NAVIGATE_CHANNEL, (target: unknown) => {
      const next = resolveTarget(target, platform)
      if (!next) return
      setState({ topNavId: next.topNavId, subNavId: next.subNavId })
      setAction(pendingOf(next))
    })
    return () => unsubscribe?.()
  }, [platform])

  const setTopNav = useCallback((topNavId: TopNavId) => {
    setState((prev) => {
      if (prev.topNavId === topNavId) return prev
      const tab = findTopNav(topNavId)
      if (!tab) return prev
      return { topNavId: tab.id, subNavId: firstVisibleSub(tab.id, platform) }
    })
  }, [platform])

  const setSubNav = useCallback((subNavId: SubNavId) => {
    setState((prev) => {
      if (prev.subNavId === subNavId) return prev
      const sub = findVisibleSubNav(prev.topNavId, subNavId, platform)
      if (!sub) return prev
      return { ...prev, subNavId: sub.id }
    })
  }, [platform])

  return {
    topNavId: state.topNavId,
    subNavId: state.subNavId,
    /** Was die Seite gleich tun soll — sie prüft selbst, ob es ihr gilt. */
    action,
    setTopNav,
    setSubNav,
  }
}

// Exported for tests so they can clear stored state between runs.
export const PREFERENCES_NAV_STORAGE_KEY = STORAGE_KEY
